import Person from "./Person";
import INetWorth from "./INetWorth";
import Model from "./Model";
import {isoToLocalDate} from "../stores/StoreUtilities";
import IModelItem from "./IModelItem";
import {AssetIncome, AssetIncomeDetail} from "../API";
import cloneDeep from "lodash.clonedeep";
import {IncomeType} from "./Income";

export enum Risk {
  Low = 0,
  Moderate = 1,
  Aggressive = 2,
  Emergency = 3
}

export enum AssetCategory {
  LiquidInvestableAssets = 1,
  RealEstateAndProperty = 2,
  PersonalProperty = 3,
  OtherAssets = 4
}

export enum AssetIncomeUnits {
  NominalDollars,
  NominalRate
}

export enum AssetType {
  None = 0,
  Brokerage = 1,
  Checking= 2,
  HSA= 3,
  IRANonSpouseInheritedBefore2020 = 4,
  RothIRANonSpouseInheritedBefore2020 = 5,
  MoneyMarket = 6,
  NonQualifiedAnnuity = 7,
  RothIRA = 8,
  RothTSP = 9,
  Roth401k = 10,
  Savings= 11,
  SEPIRA = 12,
  SimpleIRA = 13,
  TraditionalIRA = 14,
  TSP = 15,
  Form1099 = 16,
  Plan401k = 17,
  Plan403b = 18,
  Plan457 = 19,
  Plan529 = 20,
  Roth403b = 21,
  IRANonSpouseInheritedAfter2019 = 22,
  RothIRANonSpouseInheritedAfter2019 = 23
}

interface IAsset {
  id: string
  createdAt: string
  updatedAt: string
  accountId: string
  userId: string
  modelId: string
  assetCategory: AssetCategory
  assetType: AssetType
  description: string
  risk: Risk
  balance: number
  balanceDate: string
  returnRate: number
  rateLock?: boolean
  withdrawalOrder: number
  inheritedYear: number
  originalOwnerBirthYear: number
  sortOrder: number
  ownerId: string
  start: string
  end: string
  priorEoYBalance?: number
  withdrawalsYTD?: number
  managed?: boolean
  archived: boolean
  assetIncome?: AssetIncome
  // assetIncomeDate?: string
  notes?: string
  netWorthExcluded?: boolean
}

class Asset implements IModelItem, INetWorth {
  id: string
  createdAt: string
  updatedAt: string
  accountId: string
  userId: string
  modelId: string
  model?: Model
  assetCategory: AssetCategory
  assetType: AssetType
  assetTypeDef: AssetTypeDef
  description: string
  risk: Risk
  balance: number
  balanceDate: Date
  returnRate: number
  rateLock: boolean
  withdrawalOrder: number
  inheritedYear: number
  originalOwnerBirthYear: number
  sortOrder: number
  ownerId: string
  owner?: Person
  start: string
  end: string
  priorEoYBalance?: number
  withdrawalsYTD: number
  managed: boolean
  archived: boolean
  assetIncome?: AssetIncome
  // assetIncomeDate?: string
  notes?: string
  netWorthExcluded: boolean

  constructor (data: any) {
    this.id = data.id
    this.createdAt = data.createdAt
    this.updatedAt = data.updatedAt
    this.accountId = data.accountId
    this.userId = data.userId
    this.modelId = data.modelId
    this.model = data.model
    this.assetCategory = data.assetCategory ?? AssetCategory.OtherAssets
    this.assetType = data.assetType ?? AssetType.None
    this.assetTypeDef = getAssetTypeDef(this.assetType)
    this.description = data.description ?? this.assetTypeDef.label ?? ""
    this.risk = data.risk
    this.balance = data.balance ?? 0
    this.balanceDate = data.balanceDate ? isoToLocalDate(data.balanceDate) : isoToLocalDate(data.updatedAt)
    this.returnRate = data.returnRate
    this.rateLock = data.rateLock ?? false
    this.withdrawalOrder = data.withdrawalOrder ?? 0
    this.inheritedYear = this.assetTypeDef && this.assetTypeDef.requireExtraFields ? data.inheritedYear : undefined
    this.originalOwnerBirthYear = data.originalOwnerBirthYear
    this.sortOrder = data.sortOrder
    this.ownerId = data.ownerId
    this.start = data.start
    this.end = data.end
    this.priorEoYBalance = data.priorEoYBalance
    this.withdrawalsYTD = data.withdrawalsYTD ?? 0
    this.managed = data.managed ?? false
    this.archived = data.archived === true
    this.netWorthExcluded = data.netWorthExcluded === true
    this.assetIncome = cloneDeep(data.assetIncome)
    // this.assetIncomeDate = data.assetIncomeDate
    this.notes = data.notes
  }

  getAssetIncomeDetail(incomeType: IncomeType) {
    if (this.assetIncome && this.assetIncome.details) {
      let assetIncomeDetail = this.assetIncome.details.find((detail) => detail && detail.incomeType === incomeType)
      if (assetIncomeDetail) {
        return assetIncomeDetail
      } else {
        return {
          incomeType: incomeType,
          amount: 0,
          rate: 0
        }
      }
    } else {
      return {
        incomeType: incomeType,
        amount: 0,
        rate: 0
      }
    }
  }

  getTypename(): string {
    return "Asset"
  }

  toJSON(): IAsset {
    return {
      id: this.id,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      accountId: this.accountId,
      userId: this.userId,
      modelId: this.modelId,
      assetCategory: this.assetCategory,
      assetType: this.assetType,
      description: this.description,
      risk: this.risk,
      balance: this.balance,
      balanceDate: this.balanceDate ? this.balanceDate.toISOString() : "",
      returnRate: this.returnRate,
      rateLock: this.rateLock,
      withdrawalOrder: this.withdrawalOrder,
      inheritedYear: this.inheritedYear,
      originalOwnerBirthYear: this.originalOwnerBirthYear,
      sortOrder: this.sortOrder,
      ownerId: this.ownerId,
      start: this.start,
      end: this.end,
      priorEoYBalance: this.priorEoYBalance,
      withdrawalsYTD: this.withdrawalsYTD,
      managed: this.managed,
      archived: this.archived,
      netWorthExcluded: this.netWorthExcluded,
      assetIncome: this.assetIncome,
      // assetIncomeDate: this.assetIncomeDate,
      notes: this.notes
    }
  }
  get startDate(): Date | undefined {
    return this.model ? this.model.getDate(this.start) : undefined
  }

  get endDate(): Date | undefined {
    return this.model ? this.model.getDate(this.end) : undefined
  }
}

export class AssetTypeDef {
  assetType: AssetType
  label: string
  description: string
  withdrawalOrder: number
  hasRMD: boolean
  useBalanceYStart: boolean
  allowJoint: boolean
  allowContributions: boolean
  defaultEndYearIsRetired: boolean
  requireExtraFields: boolean
  useTable1Once: boolean
  considerSpouseTable: boolean
  use10YDist: boolean
  rmdBeginAge: number
  taxable: boolean
  taxFree: boolean
  taxDeferred: boolean
  isIRA: boolean
  isTaxableIRA: boolean
  isUnqualified: boolean
  isTraditionalIRA: boolean
  qcdAllowed: boolean
  convertableTo: AssetType[]
  constructor (data: any) {
    this.assetType = data.assetType ?? AssetType.None
    this.label = data.label ?? this.assetType.toString()
    this.description = data.description ?? ""
    this.withdrawalOrder = data.withdrawalOrder ?? 0
    this.hasRMD = data.hasRMD ?? false
    this.useBalanceYStart = data.useBalanceYStart ?? false
    this.allowJoint = data.allowJoint ?? false
    this.allowContributions = data.allowContributions ?? false
    this.defaultEndYearIsRetired = data.defaultEndYearIsRetired ?? false
    this.requireExtraFields = data.requireExtraFields ?? false
    this.useTable1Once = data.useTable1Once ?? false
    this.considerSpouseTable = data.considerSpouseTable ?? false
    this.use10YDist = data.use10YDist ?? false
    this.rmdBeginAge = data.rmdBeginAge ?? 0
    this.taxable = data.taxable ?? false
    this.taxFree = data.taxFree ?? false
    this.taxDeferred = data.taxDeferred ?? false
    this.isIRA = data.isIRA
    this.isTaxableIRA = data.isTaxableIRA
    this.isUnqualified = data.isUnqualified
    this.isTraditionalIRA = data.isTraditionalIRA ?? false
    this.qcdAllowed = data.qcdAllowed ?? false
    this.convertableTo = data.convertableTo ?? []
  }
}

export const AssetTypeDefs: AssetTypeDef[] = [
  new AssetTypeDef({assetType: AssetType.None,
    label: "Other",
    description: "Other types of assets not covered by specific categories",
    withdrawalOrder: 99,
    hasRMD: false,
    useBalanceYStart: false,
    allowJoint: true,
    allowContributions: true,
    defaultEndYearIsRetired: false,
    requireExtraFields: false,
    useTable1Once: false,
    considerSpouseTable: false,
    use10YDist: false,
    rmdBeginAge: 0,
    taxable: true,
    taxFree: false,
    taxDeferred: false,
    isIRA: false,
    isTaxableIRA: false,
    isUnqualified: false,
    convertableTo: []
  }),
  new AssetTypeDef({assetType: AssetType.Brokerage, label: "Brokerage", 
    description: "Investment account that allows trading of various securities including stocks, bonds, ETFs, and mutual funds",
    withdrawalOrder: 5,
    hasRMD: false,
    useBalanceYStart: true,
    allowJoint: true,
    allowContributions: true,
    defaultEndYearIsRetired: false,
    requireExtraFields: false,
    useTable1Once: false,
    considerSpouseTable: false,
    use10YDist: false,
    rmdBeginAge: 0,
    taxable: true,
    taxFree: false,
    taxDeferred: false,
    isIRA: false,
    isTaxableIRA: false,
    isUnqualified: false,
    convertableTo: [AssetType.Brokerage, AssetType.Checking, AssetType.Savings, AssetType.MoneyMarket, AssetType.NonQualifiedAnnuity]
  }),
  new AssetTypeDef({assetType: AssetType.Checking, label: "Checking", 
    description: "Bank account for everyday transactions",
    withdrawalOrder: 1,
    hasRMD: false,
    useBalanceYStart: true,
    allowJoint: true,
    allowContributions: true,
    defaultEndYearIsRetired: false,
    requireExtraFields: false,
    useTable1Once: false,
    considerSpouseTable: false,
    use10YDist: false,
    rmdBeginAge: 0,
    taxable: true,
    taxFree: false,
    taxDeferred: false,
    isIRA: false,
    isTaxableIRA: false,
    isUnqualified: false,
    convertableTo: [AssetType.Brokerage, AssetType.Checking, AssetType.Savings, AssetType.MoneyMarket, AssetType.NonQualifiedAnnuity]
  }),
  new AssetTypeDef({assetType: AssetType.HSA, label: "HSA", 
    description: "Health Savings Account",
    withdrawalOrder: 23,
    hasRMD: false,
    useBalanceYStart: false,
    allowJoint: false,
    allowContributions: true,
    defaultEndYearIsRetired: false,
    requireExtraFields: false,
    useTable1Once: false,
    considerSpouseTable: false,
    use10YDist: false,
    rmdBeginAge: 0,
    taxable: false,
    taxFree: false,
    taxDeferred: false,
    isIRA: false,
    isTaxableIRA: false,
    isUnqualified: false,
    convertableTo: [AssetType.Brokerage, AssetType.Checking, AssetType.Savings, AssetType.MoneyMarket]
  }),
  new AssetTypeDef({assetType: AssetType.IRANonSpouseInheritedBefore2020, label: "IRA Non-spouse inherited before 2020", 
    description: "Traditional IRA inherited by a non-spouse beneficiary before 2020",
    withdrawalOrder: 7,
    hasRMD: true,
    useBalanceYStart: true,
    allowJoint: false,
    allowContributions: false,
    defaultEndYearIsRetired: false,
    requireExtraFields: true,
    useTable1Once: true,
    considerSpouseTable: false,
    use10YDist: false,
    rmdBeginAge: 0,
    taxable: false,
    taxFree: false,
    taxDeferred: true,
    isIRA: true,
    isTaxableIRA: true,
    isUnqualified: false,
    qcdAllowed: true,
    convertableTo: []
  }),
  new AssetTypeDef({assetType: AssetType.RothIRANonSpouseInheritedBefore2020, label: "Roth IRA Non-spouse inherited before 2020", 
    description: "Roth IRA inherited by a non-spouse beneficiary before 2020",
    withdrawalOrder: 18,
    hasRMD: true,
    useBalanceYStart: true,
    allowJoint: false,
    allowContributions: true,
    defaultEndYearIsRetired: false,
    requireExtraFields: true,
    useTable1Once: true,
    considerSpouseTable: false,
    use10YDist: false,
    rmdBeginAge: 0,
    taxable: false,
    taxFree: true,
    taxDeferred: false,
    isIRA: true,
    isTaxableIRA: false,
    isUnqualified: false,
    qcdAllowed: true,
    convertableTo: []
  }),
  new AssetTypeDef({assetType: AssetType.MoneyMarket, label: "Money Market", 
    description: "Low-risk investment account that invests in short-term debt securities",
    withdrawalOrder: 4,
    hasRMD: false,
    useBalanceYStart: true,
    allowJoint: true,
    allowContributions: true,
    defaultEndYearIsRetired: false,
    requireExtraFields: false,
    useTable1Once: false,
    considerSpouseTable: false,
    use10YDist: false,
    rmdBeginAge: 0,
    taxable: true,
    taxFree: false,
    taxDeferred: false,
    isIRA: false,
    isTaxableIRA: false,
    isUnqualified: false,
    convertableTo: [AssetType.Brokerage, AssetType.Checking, AssetType.Savings, AssetType.MoneyMarket, AssetType.NonQualifiedAnnuity]
  }),
  new AssetTypeDef({assetType: AssetType.NonQualifiedAnnuity, label: "Non Qualified Annuity", 
    description: "Tax-deferred insurance product funded that provides regular income payments",
    withdrawalOrder: 15,
    hasRMD: false,
    useBalanceYStart: false,
    allowJoint: true,
    allowContributions: true,
    defaultEndYearIsRetired: true,
    requireExtraFields: false,
    useTable1Once: false,
    considerSpouseTable: false,
    use10YDist: false,
    rmdBeginAge: 0,
    taxable: false,
    taxFree: false,
    taxDeferred: true,
    isIRA: false,
    isTaxableIRA: false,
    isUnqualified: true,
    convertableTo: [AssetType.Brokerage, AssetType.Checking, AssetType.Savings, AssetType.MoneyMarket]
  }),
  new AssetTypeDef({assetType: AssetType.RothIRA, label: "Roth IRA", 
    description: "Individual retirement account funded with after-tax contributions that grows tax-free with qualified tax-free withdrawals in retirement",
    withdrawalOrder: 19,
    hasRMD: false,
    useBalanceYStart: true,
    allowJoint: false,
    allowContributions: true,
    defaultEndYearIsRetired: true,
    requireExtraFields: false,
    useTable1Once: false,
    considerSpouseTable: false,
    use10YDist: false,
    rmdBeginAge: 0,
    taxable: false,
    taxFree: true,
    taxDeferred: false,
    isIRA: true,
    isTaxableIRA: false,
    isUnqualified: false,
    qcdAllowed: true,
    convertableTo: []
  }),
  new AssetTypeDef({assetType: AssetType.RothTSP, label: "Roth TSP", 
    description: "Roth version of the Thrift Savings Plan for federal employees and uniformed services, offering tax-free growth and withdrawals",
    withdrawalOrder: 20,
    hasRMD: false,
    useBalanceYStart: true,
    allowJoint: false,
    allowContributions: true,
    defaultEndYearIsRetired: true,
    requireExtraFields: false,
    useTable1Once: false,
    considerSpouseTable: true,
    use10YDist: false,
    rmdBeginAge: 0,
    taxable: false,
    taxFree: true,
    taxDeferred: false,
    isIRA: true,
    isTaxableIRA: false,
    isUnqualified: false,
    convertableTo: [AssetType.RothIRA]
  }),
  new AssetTypeDef({assetType: AssetType.Roth401k, label: "Roth 401k", 
    description: "Employer-sponsored retirement plan funded with after-tax contributions that grows tax-free with qualified tax-free withdrawals",
    withdrawalOrder: 21,
    hasRMD: false,
    useBalanceYStart: true,
    allowJoint: false,
    allowContributions: true,
    defaultEndYearIsRetired: true,
    requireExtraFields: false,
    useTable1Once: false,
    considerSpouseTable: true,
    use10YDist: false,
    rmdBeginAge: 0,
    taxable: false,
    taxFree: true,
    taxDeferred: false,
    isIRA: true,
    isTaxableIRA: false,
    isUnqualified: false,
    convertableTo: [AssetType.RothIRA]
  }),
  new AssetTypeDef({assetType: AssetType.Savings, label: "Savings", 
    description: "Basic bank account for storing money with interest earnings and limited transactions",
    withdrawalOrder: 2,
    hasRMD: false,
    useBalanceYStart: true,
    allowJoint: true,
    allowContributions: true,
    defaultEndYearIsRetired: false,
    requireExtraFields: false,
    useTable1Once: false,
    considerSpouseTable: true,
    use10YDist: false,
    rmdBeginAge: 0,
    taxable: true,
    taxFree: false,
    taxDeferred: false,
    isIRA: false,
    isTaxableIRA: false,
    isUnqualified: false,
    convertableTo: [AssetType.Brokerage, AssetType.Checking, AssetType.Savings, AssetType.MoneyMarket, AssetType.NonQualifiedAnnuity]
  }),
  new AssetTypeDef({assetType: AssetType.SEPIRA, label: "SEP IRA", 
    description: "Simplified Employee Pension IRA for self-employed individuals and small business owners with higher contribution limits",
    withdrawalOrder: 8,
    hasRMD: true,
    useBalanceYStart: true,
    allowJoint: false,
    allowContributions: true,
    defaultEndYearIsRetired: true,
    requireExtraFields: false,
    useTable1Once: false,
    considerSpouseTable: true,
    use10YDist: false,
    rmdBeginAge: 73,
    taxable: false,
    taxFree: false,
    taxDeferred: true,
    isIRA: true,
    isTaxableIRA: true,
    isUnqualified: false,
    qcdAllowed: true,
    convertableTo: [AssetType.RothIRA, AssetType.SimpleIRA, AssetType.TraditionalIRA, AssetType.Plan401k, AssetType.Plan403b, AssetType.Plan457]
  }),
  new AssetTypeDef({assetType: AssetType.SimpleIRA,  label: "Simple IRA", 
    description: "Savings Incentive Match Plan for Employees IRA for small businesses with fewer than 100 employees",
    withdrawalOrder: 9,
    hasRMD: true,
    useBalanceYStart: true,
    allowJoint: false,
    allowContributions: true,
    defaultEndYearIsRetired: true,
    requireExtraFields: false,
    useTable1Once: false,
    considerSpouseTable: true,
    use10YDist: false,
    rmdBeginAge: 73,
    taxable: false,
    taxFree: false,
    taxDeferred: true,
    isIRA: true,
    isTaxableIRA: true,
    isUnqualified: false,
    qcdAllowed: true,
    convertableTo: [AssetType.RothIRA, AssetType.SEPIRA, AssetType.SimpleIRA, AssetType.TraditionalIRA, AssetType.TSP, AssetType.Plan401k, AssetType.Plan403b, AssetType.Plan457]
  }),
  new AssetTypeDef({assetType: AssetType.TraditionalIRA, label: "Traditional IRA", 
    description: "Individual retirement account funded with pre-tax or after-tax contributions that grows tax-deferred until withdrawal",
    withdrawalOrder: 10,
    hasRMD: true,
    useBalanceYStart: true,
    allowJoint: false,
    allowContributions: true,
    defaultEndYearIsRetired: true,
    requireExtraFields: false,
    useTable1Once: false,
    considerSpouseTable: true,
    use10YDist: false,
    rmdBeginAge: 73,
    taxable: false,
    taxFree: false,
    taxDeferred: true,
    isIRA: true,
    isTaxableIRA: true,
    isUnqualified: false,
    isTraditionalIRA: true,
    qcdAllowed: true,
    convertableTo: [AssetType.HSA, AssetType.RothIRA, AssetType.SimpleIRA, AssetType.TraditionalIRA, AssetType.TSP, AssetType.Plan401k, AssetType.Plan403b, AssetType.Plan457]
  }),
  new AssetTypeDef({assetType: AssetType.TSP, label: "TSP", 
    description: "Thrift Savings Plan - a retirement savings plan for federal employees and uniformed services members",
    withdrawalOrder: 11,
    hasRMD: true,
    useBalanceYStart: true,
    allowJoint: false,
    allowContributions: true,
    defaultEndYearIsRetired: true,
    requireExtraFields: false,
    useTable1Once: false,
    considerSpouseTable: true,
    use10YDist: false,
    rmdBeginAge: 73,
    taxable: false,
    taxFree: false,
    taxDeferred: true,
    isIRA: true,
    isTaxableIRA: true,
    isUnqualified: false,
    convertableTo: [AssetType.RothIRA, AssetType.TraditionalIRA]
  }),
  new AssetTypeDef({assetType: AssetType.Form1099, label: "1099", 
    description: "Income reported on Form 1099, typically representing self-employment or independent contractor earnings",
    withdrawalOrder: 3,
    hasRMD: false,
    useBalanceYStart: false,
    allowJoint: true,
    allowContributions: true,
    defaultEndYearIsRetired: false,
    requireExtraFields: false,
    useTable1Once: false,
    considerSpouseTable: true,
    use10YDist: false,
    rmdBeginAge: 0,
    taxable: true,
    taxFree: false,
    taxDeferred: false,
    isIRA: false,
    isTaxableIRA: false,
    isUnqualified: false,
    convertableTo: []
  }),
  new AssetTypeDef({assetType: AssetType.Plan401k, label: "401k", 
    description: "Employer-sponsored retirement plan that allows employees to save and invest a portion of their paycheck before taxes",
    withdrawalOrder: 14,
    hasRMD: true,
    useBalanceYStart: true,
    allowJoint: false,
    allowContributions: true,
    defaultEndYearIsRetired: true,
    requireExtraFields: false,
    useTable1Once: false,
    considerSpouseTable: true,
    use10YDist: false,
    rmdBeginAge: 73,
    taxable: false,
    taxFree: false,
    taxDeferred: true,
    isIRA: true,
    isTaxableIRA: true,
    isUnqualified: false,
    convertableTo: [AssetType.RothIRA, AssetType.Roth401k, AssetType.SEPIRA, AssetType.SimpleIRA, AssetType.TraditionalIRA]
  }),
  new AssetTypeDef({assetType: AssetType.Plan403b, label: "403b", 
    description: "Tax-advantaged retirement plan for employees of public schools, tax-exempt organizations, and certain ministers",
    withdrawalOrder: 12,
    hasRMD: true,
    useBalanceYStart: true,
    allowJoint: false,
    allowContributions: true,
    defaultEndYearIsRetired: true,
    requireExtraFields: false,
    useTable1Once: false,
    considerSpouseTable: true,
    use10YDist: false,
    rmdBeginAge: 73,
    taxable: false,
    taxFree: false,
    taxDeferred: true,
    isIRA: true,
    isTaxableIRA: true,
    isUnqualified: false,
    convertableTo: [AssetType.RothIRA, AssetType.SEPIRA, AssetType.SimpleIRA, AssetType.TraditionalIRA, AssetType.Roth403b]
  }),
  new AssetTypeDef({assetType: AssetType.Plan457, label: "457 Plan",
    description: "Tax-advantaged deferred compensation retirement plan for state and local government employees and some non-profit organizations",
    withdrawalOrder: 13,
    hasRMD: true,
    useBalanceYStart: true,
    allowJoint: false,
    allowContributions: true,
    defaultEndYearIsRetired: true,
    requireExtraFields: false,
    useTable1Once: false,
    considerSpouseTable: true,
    use10YDist: false,
    rmdBeginAge: 73,
    taxable: false,
    taxFree: false,
    taxDeferred: false,
    isIRA: true,
    isTaxableIRA: true,
    isUnqualified: false,
    convertableTo: [AssetType.RothIRA, AssetType.RothTSP, AssetType.Roth401k, AssetType.SEPIRA, AssetType.SimpleIRA, AssetType.TraditionalIRA, AssetType.TSP, AssetType.Plan401k, AssetType.Plan403b, AssetType.Plan457, AssetType.Roth403b]
  }),
  new AssetTypeDef({assetType: AssetType.Plan529, label: "529 Plan",
    description: "Tax-advantaged savings plan designed to encourage saving for future education costs",
    withdrawalOrder: 16,
    hasRMD: false,
    useBalanceYStart: false,
    allowJoint: false,
    allowContributions: true,
    defaultEndYearIsRetired: false,
    requireExtraFields: false,
    useTable1Once: false,
    considerSpouseTable: true,
    use10YDist: false,
    rmdBeginAge: 0,
    taxable: false,
    taxFree: true,
    taxDeferred: false,
    isIRA: false,
    isTaxableIRA: false,
    isUnqualified: false,
    convertableTo: [AssetType.RothIRA, AssetType.SEPIRA, AssetType.SimpleIRA]
  }),
  new AssetTypeDef({assetType: AssetType.Roth403b, label: "Roth 403b", 
    description: "Roth version of the 403b plan funded with after-tax dollars that offers tax-free growth and qualified withdrawals",
    withdrawalOrder: 22,
    hasRMD: false,
    useBalanceYStart: true,
    allowJoint: false,
    allowContributions: true,
    defaultEndYearIsRetired: true,
    requireExtraFields: false,
    useTable1Once: false,
    considerSpouseTable: false,
    use10YDist: false,
    rmdBeginAge: 0,
    taxable: false,
    taxFree: true,
    taxDeferred: false,
    isIRA: true,
    isTaxableIRA: false,
    isUnqualified: false,
    convertableTo: [AssetType.RothIRA]
  }),
  new AssetTypeDef({assetType: AssetType.IRANonSpouseInheritedAfter2019, label: "IRA Non-spouse inherited after 2019", 
    description: "Traditional IRA inherited by a non-spouse beneficiary after 2019, subject to 10-year distribution rule",
    withdrawalOrder: 6,
    hasRMD: true,
    useBalanceYStart: true,
    allowJoint: false,
    allowContributions: false,
    defaultEndYearIsRetired: false,
    requireExtraFields: true,
    useTable1Once: false,
    considerSpouseTable: false,
    use10YDist: true,
    rmdBeginAge: 0,
    taxable: false,
    taxFree: false,
    taxDeferred: true,
    isIRA: true,
    isTaxableIRA: true,
    isUnqualified: false,
    qcdAllowed: true,
    convertableTo: []
  }),
  new AssetTypeDef({assetType: AssetType.RothIRANonSpouseInheritedAfter2019, label: "Roth IRA Non-spouse inherited after 2019", 
    description: "Roth IRA inherited by a non-spouse beneficiary after 2019, subject to 10-year distribution rule but with tax-free distributions",
    withdrawalOrder: 17,
    hasRMD: true,
    useBalanceYStart: true,
    allowJoint: false,
    allowContributions: false,
    defaultEndYearIsRetired: false,
    requireExtraFields: true,
    useTable1Once: false,
    considerSpouseTable: false,
    use10YDist: true,
    rmdBeginAge: 0,
    taxable: false,
    taxFree: true,
    taxDeferred: false,
    isIRA: true,
    isTaxableIRA: false,
    isUnqualified: false,
    qcdAllowed: true,
    convertableTo: []
  })
]

export function listAssetTypes(): AssetType[] {
  return AssetTypeDefs.sort((a, b) => a.label.localeCompare(b.label))
    .map((a) => a.assetType)
}

export function listAssetTypeDefs(): AssetTypeDef[] {
  return AssetTypeDefs.sort((a, b) => a.label.localeCompare(b.label))
}

export function getAssetTypeDef(assetType: AssetType) : AssetTypeDef {
  const assetTypeDef = AssetTypeDefs.find((item: AssetTypeDef) => item.assetType === assetType)
  return assetTypeDef ?? AssetTypeDefs[0]
}

export function getAssetTypeLabel(assetType: AssetType): string {
  const assetTypeDef = AssetTypeDefs.find((def: AssetTypeDef) => def.assetType === assetType)
  return assetTypeDef?.label ?? ""
}

export default Asset